import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sentry } from "@/lib/sentry";

// Pin the auth-email redirect to the canonical .org host so verification
// links work no matter which host (lovable.app, dev, .org) the user
// signed up from. Override via VITE_APP_URL for local dev.
const APP_URL = import.meta.env.VITE_APP_URL || "https://fbn-entrepreneurs.org";

export interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  fbnChapter: string;
  city?: string;
  segment?: string[];
  bio?: string;
  workingOnProject?: boolean;
  projectType?: string;
  projectDescription?: string;
  sdgGoals?: string[];
  linkedinUrl?: string;
  websiteUrl?: string;
  industryInterests?: string[];
  phoneNumber?: string;
}

export const useAuthOperations = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const signUp = async (data: SignupData) => {
    setLoading(true);
    try {
      // Step 1: Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: APP_URL,
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            fbn_chapter: data.fbnChapter,
            city: data.city || null,
            segment: data.segment || [],
            bio: data.bio || null,
            working_on_project: data.workingOnProject || false,
            project_type: data.projectType || null,
            project_description: data.projectDescription || null,
            sdg_goals: data.sdgGoals || [],
            linkedin_url: data.linkedinUrl || null,
            website_url: data.websiteUrl || null,
            industry_interests: data.industryInterests || [],
            phone_number: data.phoneNumber || null,
            pending_legal_acceptance: true,
          },
        },
      });

      if (authError) {
        Sentry.captureException(authError, {
          tags: { area: "auth_signup" },
          extra: { hasEmail: !!data.email, hasChapter: !!data.fbnChapter },
        });
        throw authError;
      }

      // Verification email via Resend (NOT Supabase's built-in SMTP) — see
      // supabase/functions/signup-verification/index.ts. Best-effort: failure
      // here doesn't block signup, the auth.users row is the source of truth
      // and the user can request a resend.
      try {
        await supabase.functions.invoke("signup-verification", {
          body: {
            email: data.email,
            first_name: data.firstName,
            redirect_to: APP_URL,
          },
        });
      } catch {
        // Best-effort; user can re-trigger from the auth screen.
      }

      // Best-effort admin email notification of the new signup.
      // Fires in parallel with the toast - failure won't block the user.
      try {
        await supabase.functions.invoke("admin-notify", {
          body: {
            kind: "new_signup",
            member_name: `${data.firstName} ${data.lastName}`.trim(),
            member_email: data.email,
            chapter: data.fbnChapter,
          },
        });
      } catch {
        // Email is best-effort; the auth.users + profile rows are the source of truth.
      }

      toast({
        title: "Check your email",
        description: "We've sent you a verification link to confirm your account.",
      });

      return { success: true, user: authData.user };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Something went wrong. Please try again.";
      toast({
        title: "Signup failed",
        description: message,
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "You're successfully logged in",
      });

      return { success: true, user: data.user };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Invalid email or password.";
      toast({
        title: "Log in failed",
        description: message,
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  return { signUp, signIn, loading };
};
